import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Popover } from "radix-ui";
import type { CommunityStructure, Conversation, Member } from "@atrium/shared";
import { api, errorMessage } from "../../lib/api";
import { cn } from "../../lib/cn";
import { Avatar, type PresenceStatus } from "../../components/ui/Avatar";
import { Button } from "../../components/ui/Button";
import { confirm } from "../../components/ui/Dialog";
import { toast } from "../../components/ui/Toast";
import { RowSkeleton } from "../../components/ui/Skeleton";
import { NameTags } from "../../components/UserBadges";
import { IconBot, IconCrown, IconMessages, IconTimer, IconUser } from "../../components/icons";
import { Menu, MenuContent, MenuItem, MenuLabel, MenuTrigger } from "../../components/ui/Menu";
import { useMe } from "../auth/AuthProvider";
import { permsOf, useMembers } from "./hooks";
import { shiftClickMention } from "../chat/composerBus";
import { UserContextMenu } from "../chat/MessageMenus";

/** The status the member shows to others: online, idle, do not disturb or offline. */
export const memberStatus = (m: Member): PresenceStatus => m.status ?? (m.online ? "online" : "offline");

/** Highest role that's shown separately in the member list. */
function topHoisted(member: Member, roles: CommunityStructure["roles"]) {
  return roles.filter((r) => !r.isDefault && r.hoist && member.roleIds.includes(r.id)).sort((a, b) => b.position - a.position)[0] ?? null;
}

/** Name colour: the highest role that has a colour. */
export function roleColour(roleIds: string[], roles: CommunityStructure["roles"]): string | null {
  return roles.filter((r) => !r.isDefault && r.color && roleIds.includes(r.id)).sort((a, b) => b.position - a.position)[0]?.color ?? null;
}

const TIMEOUTS = [
  { m: 1, l: "1 minute" },
  { m: 5, l: "5 minutes" },
  { m: 10, l: "10 minutes" },
  { m: 60, l: "1 hour" },
  { m: 60 * 24, l: "1 day" },
  { m: 60 * 24 * 7, l: "1 week" },
];

export function MemberCard({ member, structure, onClose }: { member: Member; structure: CommunityStructure; onClose: () => void }) {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { community, roles } = structure;
  const perms = permsOf(community.myPermissions, community.isOwner);
  const [roleIds, setRoleIds] = useState(member.roleIds);
  const self = member.user.id === me.id;
  const myTop = community.isOwner ? Infinity : Math.max(0, ...roles.filter((r) => structure.myRoleIds.includes(r.id)).map((r) => r.position));
  // A bot's own role comes with its installation and can't be handed out.
  const assignable = roles.filter((r) => !r.isDefault && !r.managed && r.position < myTop);
  const canModerate = !self && !member.isOwner;

  const toggleRole = async (id: string) => {
    const next = roleIds.includes(id) ? roleIds.filter((x) => x !== id) : [...roleIds, id];
    setRoleIds(next);
    try {
      await api.put(`/api/communities/${community.id}/members/${member.user.id}/roles`, { roleIds: next });
      void qc.invalidateQueries({ queryKey: ["members", community.id] });
      if (self) void qc.invalidateQueries({ queryKey: ["structure", community.id] });
    } catch (e) {
      setRoleIds(roleIds);
      toast.error(errorMessage(e));
    }
  };

  const message = async () => {
    try {
      const c = await api.post<Conversation>("/api/conversations", { userIds: [member.user.id] });
      onClose();
      navigate(`/messages/${c.id}`);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const timedOut = !!member.timeoutUntil && new Date(member.timeoutUntil) > new Date();
  const timeout = async (minutes: number) => {
    try {
      await api.put(`/api/communities/${community.id}/members/${member.user.id}/timeout`, { minutes });
      void qc.invalidateQueries({ queryKey: ["members", community.id] });
      toast.show(minutes ? `${member.user.displayName} is timed out` : "Time-out removed");
      onClose();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const moderate = async (kind: "kick" | "ban") => {
    const ok = await confirm({
      title: `${kind === "kick" ? "Kick" : "Ban"} ${member.user.displayName}?`,
      description: kind === "kick" ? "They can rejoin with an invite, or directly if the community is public." : "They'll be removed and won't be able to rejoin.",
      confirmLabel: kind === "kick" ? "Kick" : "Ban",
      danger: true,
    });
    if (!ok) return;
    try {
      if (kind === "kick") await api.del(`/api/communities/${community.id}/members/${member.user.id}`);
      else await api.put(`/api/communities/${community.id}/bans/${member.user.id}`, { reason: "" });
      void qc.invalidateQueries({ queryKey: ["members", community.id] });
      onClose();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <div className="w-[300px]">
      <div className="flex items-center gap-3 border-b border-line p-4">
        <Avatar src={member.user.avatarUrl} name={member.user.displayName} size="xl" status={memberStatus(member)} />
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 truncate text-base font-semibold">
            <span className="truncate">{member.user.displayName}</span>
            <NameTags user={member.user} />
            {member.isOwner && <IconCrown className="icon size-4 text-warning" aria-label="Owner" />}
          </div>
          <div className="truncate text-sm text-fg-3">@{member.user.username}</div>
        </div>
      </div>
      <div className="space-y-3 p-4">
        {(roleIds.length > 0 || (perms.manageRoles && assignable.length > 0)) && (
          <div>
            <div className="mb-1.5 text-xs font-medium text-fg-3">Roles</div>
            <div className="flex flex-wrap gap-1">
              {(perms.manageRoles ? roles.filter((r) => !r.isDefault) : roles.filter((r) => roleIds.includes(r.id))).map((r) => {
                const on = roleIds.includes(r.id);
                const editable = perms.manageRoles && !r.managed && r.position < myTop && (self || !member.isOwner);
                return (
                  <button
                    key={r.id}
                    disabled={!editable}
                    onClick={() => void toggleRole(r.id)}
                    aria-pressed={on}
                    className={cn(
                      "inline-flex h-7 items-center gap-1.5 rounded-md border px-2 text-xs font-medium transition-colors",
                      on ? "border-line-strong bg-surface-2 text-fg" : "border-dashed border-line text-fg-3",
                      editable && "hover:border-fg-3",
                      !editable && !on && "hidden",
                    )}
                  >
                    <span className="size-2 rounded-full" style={{ background: r.color ?? "var(--fg-3)" }} aria-hidden />
                    {r.name}
                    {r.managed && <IconBot className="icon size-3.5 text-fg-3" aria-label="Managed by an app" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <div className="flex gap-2">
          <Button size="sm" icon={IconUser} onClick={() => (onClose(), navigate(`/u/${member.user.username}`))}>
            Profile
          </Button>
          {!self && !member.user.bot && (
            <Button size="sm" icon={IconMessages} onClick={() => void message()}>
              Message
            </Button>
          )}
        </div>
        {timedOut && (
          <div className="flex items-center gap-2 rounded-md bg-danger-soft px-2.5 py-1.5 text-xs text-danger">
            <IconTimer className="icon size-4" aria-hidden />
            Timed out until {new Date(member.timeoutUntil!).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}
          </div>
        )}
        {canModerate && (perms.kick || perms.ban || perms.timeout) && (
          <div className="flex flex-wrap gap-2 border-t border-line pt-3">
            {perms.timeout &&
              (timedOut ? (
                <Button size="sm" variant="ghost" icon={IconTimer} onClick={() => void timeout(0)}>
                  Remove time-out
                </Button>
              ) : (
                <Menu>
                  <MenuTrigger asChild>
                    <Button size="sm" variant="ghost" icon={IconTimer}>
                      Time out
                    </Button>
                  </MenuTrigger>
                  <MenuContent align="start">
                    <MenuLabel>Time out for</MenuLabel>
                    {TIMEOUTS.map((t) => (
                      <MenuItem key={t.m} onSelect={() => void timeout(t.m)}>
                        {t.l}
                      </MenuItem>
                    ))}
                  </MenuContent>
                </Menu>
              ))}
            {perms.kick && (
              <Button size="sm" variant="ghost" className="text-danger hover:bg-danger-soft hover:text-danger" onClick={() => void moderate("kick")}>
                Kick
              </Button>
            )}
            {perms.ban && (
              <Button size="sm" variant="ghost" className="text-danger hover:bg-danger-soft hover:text-danger" onClick={() => void moderate("ban")}>
                Ban
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function MemberRow({ member, structure }: { member: Member; structure: CommunityStructure }) {
  const me = useMe();
  const [open, setOpen] = useState(false);
  const colour = roleColour(member.roleIds, structure.roles);
  const { community } = structure;
  const perms = permsOf(community.myPermissions, community.isOwner);
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <UserContextMenu
        user={member.user}
        meId={me.id}
        canMention
        moderation={{
          communityId: community.id,
          kick: perms.kick,
          ban: perms.ban,
          timeout: perms.timeout,
          ownerId: member.isOwner ? member.user.id : null,
          timedOutUntil: (id) => (id === member.user.id ? member.timeoutUntil : null),
        }}
      >
        <Popover.Trigger asChild>
          <button
            {...shiftClickMention(member.user.username)}
            className={cn("flex h-10 w-full items-center gap-2.5 rounded-md px-2 text-left hover:bg-surface", !member.online && "opacity-50 hover:opacity-100")}
          >
            <Avatar src={member.user.avatarUrl} name={member.user.displayName} size="sm" status={memberStatus(member)} />
            <span className="truncate text-sm font-medium" style={{ color: colour ?? undefined }}>
              {member.user.displayName}
            </span>
            <NameTags user={member.user} />
            {member.isOwner && <IconCrown className="icon size-3.5 shrink-0 text-warning" aria-label="Owner" />}
            {member.timeoutUntil && new Date(member.timeoutUntil) > new Date() && <IconTimer className="icon size-3.5 shrink-0 text-danger" aria-label="Timed out" />}
          </button>
        </Popover.Trigger>
      </UserContextMenu>
      <Popover.Portal>
        <Popover.Content side="left" align="start" sideOffset={8} collisionPadding={8} className="z-[60] rounded-lg bg-surface shadow-pop animate-pop-in">
          <MemberCard member={member} structure={structure} onClose={() => setOpen(false)} />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export function MemberList({ structure, channelId }: { structure: CommunityStructure; channelId: string }) {
  const { data, isPending } = useMembers(structure.community.id, true, channelId);
  const members = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);

  const groups = useMemo(() => {
    const hoisted = new Map<string, Member[]>();
    const online: Member[] = [];
    const offline: Member[] = [];
    for (const m of members) {
      if (!m.online) {
        offline.push(m);
        continue;
      }
      const r = topHoisted(m, structure.roles);
      if (r) hoisted.set(r.id, [...(hoisted.get(r.id) ?? []), m]);
      else online.push(m);
    }
    const byName = (a: Member, b: Member) => a.user.displayName.localeCompare(b.user.displayName);
    const out: { title: string; list: Member[] }[] = [];
    for (const r of structure.roles) if (hoisted.has(r.id)) out.push({ title: r.name, list: hoisted.get(r.id)!.sort(byName) });
    if (online.length) out.push({ title: "Online", list: online.sort(byName) });
    if (offline.length) out.push({ title: "Offline", list: offline.sort(byName) });
    return out;
  }, [members, structure.roles]);

  return (
    <aside aria-label="Members" className="scroll-y w-64 shrink-0 border-l xl:w-72 border-line bg-bg-subtle px-2 py-3">
      {isPending
        ? Array.from({ length: 6 }, (_, i) => <RowSkeleton key={i} />)
        : groups.map((g) => (
            <div key={g.title} className="mb-4">
              <h3 className="px-2 pb-1 text-xs font-semibold text-fg-3">
                {g.title} · <span className="tabular">{g.list.length}</span>
              </h3>
              {g.list.map((m) => (
                <MemberRow key={m.user.id} member={m} structure={structure} />
              ))}
            </div>
          ))}
    </aside>
  );
}
